import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import styles from './Tooltip.module.css';

interface TooltipProps {
  text: string;
  children?: ReactNode;
  // true면 hover해도 말풍선을 띄우지 않는다 — 말줄임 여부처럼 조건부로 안내할 때.
  disabled?: boolean;
}

// 말풍선이 화면 가장자리를 넘지 않도록 두는 여백.
const EDGE = 8;

// orbit Tooltip은 항상 "?" 모양 trigger를 갖는다. children을 옵션으로 받아
// 다른 trigger로도 쓸 수 있게 확장.
//
// 말풍선은 position: fixed 로 띄우고 좌표를 직접 계산한다. absolute 로 두면
// overflow 를 가진 조상(표의 가로 스크롤 컨테이너, 패널 스크롤 영역 등)이 잘라내서
// 설명이 보이지 않는다. fixed 는 그 클리핑을 벗어난다.
export function Tooltip({ text, children, disabled = false }: TooltipProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  const show = () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    // 좌우로 화면을 넘지 않게 가둔다 — 표 오른쪽 끝 열의 설명이 잘리던 자리다.
    const half = Math.min(320, window.innerWidth - EDGE * 2) / 2;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, EDGE + half),
      window.innerWidth - EDGE - half,
    );
    setPos({ top: rect.top - 6, left });
  };

  return (
    <span
      ref={wrapRef}
      className={styles.wrapper}
      onMouseEnter={show}
      onMouseLeave={() => setPos(null)}
    >
      {children ?? <span className={styles.trigger}>?</span>}
      {pos && !disabled && (
        <span className={styles.bubble} role="tooltip" style={{ top: pos.top, left: pos.left }}>
          {text}
          <span className={styles.arrow} />
        </span>
      )}
    </span>
  );
}
